import {	
	Box,
	Container,
	Step,
	StepIconProps,
	StepLabel,
	Stepper,
	Typography,
	useTheme,
} from "@mui/material";
import * as hooks from "lib/hooks";

import { ExcelUpload } from "./ExcelUpload";
import { PreUpload } from "./PreUploadScreen";
import { UploadReviewScreen } from "./UploadReviewScreen";
import { useParams } from "react-router-dom";
import { Check, Close } from "@mui/icons-material";
import * as misc from "lib/util";
import { UseUploadPageAccess } from "./hooks/useUploadPageAccess";

export type StepperComponent = {
	Comp: React.ComponentType;
	label: string;
};

export const StepperComponents: StepperComponent[] = [
	{ Comp: PreUpload, label: "Containment Information" },
	{ Comp: UploadReviewScreen, label: "Upload, Review and Submit" },
];

const singleStepComponents: StepperComponent[] = [
	{ Comp: ExcelUpload, label: "Upload and Submit" },
];

export function UploadComponent(): React.JSX.Element {
	const { hasUnsavedChanges, reset, activeStep, stepStatus } =
		hooks.UseFormContext();
	const currRoute = hooks.UseCurrentRoute();
	const { lockMode, unlockMode, action } = hooks.UseAddSerialMode();
	const { id } = useParams();

	const isSingleStep = !isNil(id);

	const stepperComponents = isSingleStep
		? singleStepComponents
		: StepperComponents;

	const formProgressCompleted = stepStatus
		.IfLet()
		.Ok((d) => d?.formProgress === "completed")
		.UnwrapOr(false);

	hooks.UseUnsavedChanges(
		currRoute
			.IfSome(
				(r) =>
					r.Path.Equal("/upload") &&
					hasUnsavedChanges() &&
					// Checks that the user has NOT submitted the containment
					!(
						activeStep === stepperComponents.length - 1 &&
						(stepStatus.IsErr() || formProgressCompleted)
					)
			)
			.UnwrapOr(false),
		reset
	);
		
	const {
		hasUploadPageAccess,
		isCheckingUploadPageAccess,
		containmentLoadFailed,
	} = UseUploadPageAccess(action, id);

	if (isCheckingUploadPageAccess) {
		return <></>;
	}

	if (containmentLoadFailed) {
		return (
			<Box sx={{ px: 3, pt: 4, pb: 2, textAlign: "center" }}>
				<Typography
					data-test-selector="upload-page-containment-load-failed"
					component="p"
					variant="h5"
					sx={{
						color: "error.main",
						fontWeight: "bold",
					}}
				>
					We were unable to load this containment. Please refresh the page or
					try again. If the problem persists, contact a sector admin or plant
					admin.
				</Typography>
			</Box>
		);
	}

	if (!hasUploadPageAccess) {
		return (
			<Box sx={{ px: 3, pt: 4, pb: 2, textAlign: "center" }}>
				<Typography
					data-test-selector="no-upload-page-access"
					component="p"
					variant="h5"
					sx={{
						color: "error.main",
						fontWeight: "bold",
					}}
				>
					You currently do not have permission to access this upload flow.
					Please contact one of the sector admins or plant admins for access.
				</Typography>
			</Box>
		);
	}

	function customStepIcon(props: StepIconProps) {
		const { active, completed, icon, error } = props;
		const theme = useTheme();

		const isFinished = completed || error;
		const isLarge = active && !isFinished;

		const content = completed ? (
			<Check sx={{ fontSize: isLarge ? 20 : 16 }} />
		) : error ? (
			<Close sx={{ fontSize: isLarge ? 20 : 16 }} />
		) : (
			icon
		);

		const bgColor = error
			? theme.palette.error.main
			: completed
				? theme.palette.success.main
				: active
					? theme.palette.primary.main
					: theme.palette.grey[300];

		return (
			<Box
				sx={{
					width: isLarge ? 35 : 28,
					height: isLarge ? 35 : 28,
					borderRadius: "50%",
					backgroundColor: bgColor,
					color: "#fff",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					fontSize: isLarge ? 16 : 14,
					fontWeight: "bold",
					transition: "all 0.2s ease-in-out",
				}}
			>
				{content}
			</Box>
		);
	}

	/* eslint-disable-next-line */
	const Comp = stepperComponents[activeStep].Comp;
	const theme = useTheme();

	const isCreateFlow = isNil(id);

	return (
		<Container maxWidth={isCreateFlow ? "lg" : "xl"} sx={{ mt: 3 }}>
			{!isCreateFlow && (
				<Typography
					variant="h4"
					sx={{
						p: 1,
						textAlign: "center",
					}}
				>
					<b>
						{lockMode
							? "Lock LPNs"
							: unlockMode
								? "Unlock LPNs"
								: "Add Serials To Containment"}
					</b>
				</Typography>
			)}
			{stepperComponents.length > 1 && (
			<Stepper activeStep={activeStep % stepperComponents.length} sx={{ mb: 3 }}>
				{stepperComponents.Map(({ label }, index) => {
					const testId = misc.ToKebabCase(label);

					return (
						<Step key={label}>
							<StepLabel
								data-test-selector={`step-label-${testId}`}
								sx={{
									"& .MuiStepLabel-label": {
										fontWeight: 400,
										fontSize: "0.875rem",
										color: "text.secondary",
									},
									"& .MuiStepLabel-label.Mui-active": {
										fontWeight: 600,
										fontSize: "1rem",
										color: "primary.main",
									},
								}}
								StepIconComponent={customStepIcon}
								StepIconProps={{
									completed:
										index < activeStep ||
										formProgressCompleted,
								}}
								error={
									activeStep === index && stepStatus.IsErr()
								}
							>
								{label}
							</StepLabel>
						</Step>
					);
				})}
			</Stepper>
			)}
			<Comp />
		</Container>
	);
}
